import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ref, onValue, off } from 'firebase/database';
import { managementDb } from '../firebase';
import './Services.css';
import PageHero from '../components/PageHero';

function getYouTubeVideoId(url) {
  if (!url || typeof url !== 'string') return '';
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : '';
}

function Services() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentService, setCurrentService] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentVideo, setCurrentVideo] = useState(null);

  // Fetch services from Firebase
  useEffect(() => {
    const servicesRef = ref(managementDb, 'services');
    onValue(servicesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const arr = Object.keys(data).map(id => ({ id, ...data[id] })).sort((a, b) => a.order - b.order);
        setServices(arr);
        // Set default video to first service
        if (arr.length > 0 && !currentVideo) {
          setCurrentVideo({
            id: getYouTubeVideoId(arr[0].videoUrl),
            title: arr[0].title,
            description: arr[0].description
          });
        }
      } else {
        setServices([]);
      }
      setLoading(false);
    });
    return () => off(servicesRef);
    // eslint-disable-next-line
  }, []);

  // Open modal for service details
  const openModal = (service) => {
    setCurrentService(service);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentService(null);
    document.body.style.overflow = 'auto';
  };

  const handleVideoChange = (service) => {
    setCurrentVideo({
      id: getYouTubeVideoId(service.videoUrl),
      title: service.title,
      description: service.description
    });
  };

  const handleContactClick = () => {
    document.body.style.overflow = 'auto';
    navigate('/contact');
  };

  const handleGetQuoteClick = () => {
    // Navigate to the contact page and request the quote form to open
    navigate('/contact?form=quote');
  };

  // Open modal if ?service= param is present
  useEffect(() => {
    if (!services.length) return;
    const params = new URLSearchParams(location.search);
    const serviceId = params.get('service');
    if (serviceId) {
      const found = services.find(s => s.id === serviceId);
      if (found) {
        setCurrentService(found);
        setIsModalOpen(true);
        document.body.style.overflow = 'hidden';
      }
    }
    // eslint-disable-next-line
  }, [location.search, services]);

  if (loading) return <div className="services-page"><div>Loading...</div></div>;

  return (
    <div className="services-page">
      <PageHero
        title="Services built for modern productions"
        subtitle="Pick the capability you need or plug-in a full-stack crew. We flex to your storyboard, sprint, and budget."
        align="left"
      />
      {/* Service Details Modal */}
      {isModalOpen && currentService && (
        <div className="custom-modal-overlay" onClick={closeModal}>
          <div className="custom-modal-container" onClick={e => e.stopPropagation()}>
            <div className="custom-modal-header">
              <h2 className="custom-modal-title">{currentService.title}</h2>
              <button className="custom-modal-close" onClick={closeModal}>&times;</button>
            </div>
            <div className="custom-modal-body">
              <p className="custom-modal-description">{currentService.description}</p>
              <div className="custom-modal-section-title">What We Provide:</div>
              <div className="custom-features-grid">
                {currentService.features && currentService.features.map((feature, idx) => (
                  <div className="custom-feature-item" key={idx}>
                    <div className="custom-feature-icon-bg">
                      <i className={feature.icon}></i>
                    </div>
                    <div>
                      <div className="custom-feature-title">{feature.title}</div>
                      <div className="custom-feature-desc">{feature.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="custom-modal-footer">
              <button className="custom-modal-btn" onClick={handleContactClick}>
                Contact Us About This Service
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="container services-layout">
          <div className="services-overview">
            <p className="services-eyebrow">Capabilities</p>
            <h2 className="services-heading">
              Production pods that flex around your brief.
            </h2>
            <p className="services-copy">
              Explore modular crews across strategy, production, post and distribution.
              Every card opens a detailed scope, talent mix, and recent work so you know
              exactly what to expect before we roll camera.
            </p>
            <div className="services-pills">
              <span>Concept-to-delivery</span>
              <span>Multi-city crews</span>
              <span>Agency partnerships</span>
            </div>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <article
                key={service.id}
                className="service-card--modern"
                style={{ animationDelay: `${index * 80}ms` }}
              >
                <div className="service-card__icon">
                  <span>
                    <i className={service.icon}></i>
                  </span>
                </div>
                <div className="service-card__body">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                </div>
                <button
                  className="service-card__cta"
                  onClick={() => openModal(service)}
                >
                  View scope
                  <i className="fas fa-arrow-right"></i>
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Service Videos Section */}
      <section id="service-videos" className="service-videos-section">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="videos-section-title">Our Work in Action</h2>
            <p className="videos-section-subtitle">
              See examples of our professional video services. Click on any service to view a sample of our work.
            </p>
          </div>
          <div className="videos-container">
            {/* Video Display (Left Side) */}
            <div className="video-display">
              <div className="video-fade show" id="video-container">
                <div className="video-container">
                  <iframe
                    id="service-video"
                    src={`https://www.youtube.com/embed/${currentVideo ? currentVideo.id : ''}`}
                    title={currentVideo ? currentVideo.title : ''}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="video-info">
                  <h3 className="video-title">{currentVideo ? currentVideo.title : ''}</h3>
                  <p className="video-description">{currentVideo ? currentVideo.description : ''}</p>
                </div>
              </div>
            </div>
            {/* Service Options (Right Side) */}
            <div className="service-options-panel">
              <div className="service-options-header">
                <h3>Our Services</h3>
                <p>Click to view sample work</p>
              </div>
              <div className="service-options">
                {services.map((service, index) => (
                  <div
                    key={service.id}
                    className={`service-option ${currentVideo && currentVideo.id === getYouTubeVideoId(service.videoUrl) ? 'active' : ''}`}
                    onClick={() => handleVideoChange(service)}
                  >
                    <h4 className="service-option-title">{service.title}</h4>
                    <p className="service-option-description">{service.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Start Your Video Project?</h2>
            <p className="cta-description">
              Let's discuss how we can help bring your vision to life with our professional video production services.
            </p>
            <div className="cta-buttons">
              <button className="cta-btn-primary" onClick={handleGetQuoteClick}>
                Get a Quote
              </button>
              <button className="cta-btn-secondary" onClick={handleContactClick}>
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Services;